import { useMemo } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Divider,
  styled,
  tableCellClasses,
  TableCell,
} from "@mui/material";
import {
  FinancialDetailsFormFields,
  UnitDetailsFormFields,
} from "@/modules/property-tracks/hooks/rental-financial-proposal/multi-step-form.hook";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.nawy.radius.xl,
  border: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.nawy.colors.semantic.layer.secondary,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderRight: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
  borderBottom: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
  "&:last-child": {
    borderRight: "none",
  },
  "tr:last-child &": {
    borderBottom: "none",
  },
  [`&.${tableCellClasses.head}`]: {
    ...theme.nawy.typography.english.variants.body.sm.semibold,
  },
}));

interface FinancialProposalPreviewProps {
  unitDetailsForm: UseFormReturn<UnitDetailsFormFields>;
  financialDetailsForm: UseFormReturn<FinancialDetailsFormFields>;
}

interface YearlyData {
  year: number;
  totalMonthly: number;
  totalYearly: number;
  clientMonthly: number;
  clientYearly: number;
  nawyMonthly: number;
  nawyYearly: number;
  annualROI: number;
}

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const formatCurrency = (value: number, currency: string = "EGP"): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatMillions = (value: number) =>
  `${(value / 1_000_000).toFixed(1).toLocaleString()} M`;

export default function FinancialProposalPreview({
  unitDetailsForm,
  financialDetailsForm,
}: FinancialProposalPreviewProps) {
  // Watch unit details fields
  const [finishingCost, furnishingCost] = useWatch({
    control: unitDetailsForm.control,
    name: ["finishingCost", "furnishingCost"],
    // defaultValue: [0, 0],
  });

  // Watch financial details fields
  const [
    rentalAmount,
    annualIncreasePercent,
    rentalYears,
    clientCostSharePercent,
    clientRevenueSharePercent,
  ] = useWatch({
    control: financialDetailsForm.control,
    name: [
      "rentalAmount",
      "annualIncreasePercent",
      "rentalYears",
      "clientCostSharePercent",
      "clientRevenueSharePercent",
    ],
    // defaultValue: [0, 0, 0, 0, 0],
  });

  // Memoize parsed values
  const formValues = useMemo(
    () => ({
      finishingCost: Number(finishingCost) || 0,
      furnishingCost: Number(furnishingCost) || 0,
      rentalAmount: Number(rentalAmount) || 0,
      annualIncreasePercent: Number(annualIncreasePercent) || 0,
      rentalYears: Number(rentalYears) || 0,
      clientCostSharePercent: Number(clientCostSharePercent) || 0,
      clientRevenueSharePercent: Number(clientRevenueSharePercent) || 0,
    }),
    [
      finishingCost,
      furnishingCost,
      rentalAmount,
      annualIncreasePercent,
      rentalYears,
      clientCostSharePercent,
      clientRevenueSharePercent,
    ]
  );

  // Memoize investment calculations
  const investmentData = useMemo(() => {
    const totalInvestment =
      formValues.finishingCost + formValues.furnishingCost;
    const ownersInvestment =
      totalInvestment * (formValues.clientCostSharePercent / 100);

    return { totalInvestment, ownersInvestment };
  }, [
    formValues.finishingCost,
    formValues.furnishingCost,
    formValues.clientCostSharePercent,
  ]);

  // Memoize yearly data calculations
  const { yearlyData, totalNet } = useMemo(() => {
    const data: YearlyData[] = [];
    let currMonthly = formValues.rentalAmount;
    let currYearly = formValues.rentalAmount * 12;
    let accumulatedNet = 0;

    for (let year = 1; year <= formValues.rentalYears; year++) {
      const clientYield =
        currYearly * (formValues.clientRevenueSharePercent / 100);
      const nawyYield =
        currYearly * ((100 - formValues.clientRevenueSharePercent) / 100);
      const clientMonthly =
        currMonthly * (formValues.clientRevenueSharePercent / 100);
      const nawyMonthly =
        currMonthly * ((100 - formValues.clientRevenueSharePercent) / 100);
      const annualROI =
        investmentData.ownersInvestment > 0
          ? (clientYield / investmentData.ownersInvestment) * 100
          : 0;

      data.push({
        year,
        totalMonthly: currMonthly,
        totalYearly: currYearly,
        clientMonthly,
        clientYearly: clientYield,
        nawyMonthly,
        nawyYearly: nawyYield,
        annualROI,
      });

      accumulatedNet += clientYield;
      currMonthly *= 1 + formValues.annualIncreasePercent / 100;
      currYearly = currMonthly * 12;
    }

    return { yearlyData: data, totalNet: accumulatedNet };
  }, [
    formValues.rentalAmount,
    formValues.rentalYears,
    formValues.clientRevenueSharePercent,
    formValues.annualIncreasePercent,
    investmentData.ownersInvestment,
  ]);

  return (
    <Box>
      {/* Total Yield Table */}
      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.sm,
          alignItems: "stretch",
        })}
      >
        <Typography
          sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
        >
          Total Yield
        </Typography>
        <StyledTableContainer>
          <Table>
            <StyledTableHead>
              <TableRow>
                <StyledTableCell />
                <StyledTableCell>Monthly</StyledTableCell>
                <StyledTableCell>Annually</StyledTableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {yearlyData.map((data) => (
                <TableRow key={data.year}>
                  <StyledTableCell>Year {data.year}</StyledTableCell>
                  <StyledTableCell>
                    {formatCurrency(data.totalMonthly)}
                  </StyledTableCell>
                  <StyledTableCell>
                    {formatCurrency(data.totalYearly)}
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </Box>

      <Divider sx={(t) => ({ my: t.nawy.spacing.lg })} />

      {/* Client Yield Table */}
      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.sm,
        })}
      >
        <Typography
          sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
        >
          Client Yield
        </Typography>
        <StyledTableContainer>
          <Table>
            <StyledTableHead>
              <TableRow>
                <StyledTableCell />
                <StyledTableCell>Monthly</StyledTableCell>
                <StyledTableCell>Annually</StyledTableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {yearlyData.map((data) => (
                <TableRow key={data.year}>
                  <StyledTableCell>Year {data.year}</StyledTableCell>
                  <StyledTableCell>
                    {formatCurrency(data.clientMonthly)}
                  </StyledTableCell>
                  <StyledTableCell>
                    {formatCurrency(data.clientYearly)}
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </Box>

      <Divider sx={(t) => ({ my: t.nawy.spacing.lg })} />

      {/* Annual Yield ROI Table */}
      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.sm,
        })}
      >
        <Typography
          sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
        >
          Annual Yield ROI
        </Typography>
        <StyledTableContainer>
          <Table>
            <StyledTableHead>
              <TableRow>
                <StyledTableCell />
                <StyledTableCell>Total Revenue</StyledTableCell>
                <StyledTableCell>Client Share</StyledTableCell>
                <StyledTableCell>ROI %</StyledTableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {yearlyData.map((data) => (
                <TableRow key={data.year}>
                  <StyledTableCell>Year {data.year}</StyledTableCell>
                  <StyledTableCell>
                    {formatCurrency(data.totalYearly)}
                  </StyledTableCell>
                  <StyledTableCell>
                    {formatCurrency(data.clientYearly)}
                  </StyledTableCell>
                  <StyledTableCell>
                    {formatPercent(data.annualROI)}
                  </StyledTableCell>
                </TableRow>
              ))}
              <TableRow>
                <StyledTableCell colSpan={4}>
                  Total net return over {formValues.rentalYears} years{" "}
                  {formatMillions(totalNet)}
                </StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </StyledTableContainer>
      </Box>

      <Divider sx={(t) => ({ my: t.nawy.spacing.lg })} />

      {/* Nawy Unlocked Yield Table */}
      <Box
        sx={(t) => ({
          display: "flex",
          flexDirection: "column",
          gap: t.nawy.spacing.sm,
        })}
      >
        <Box>
          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.md.semibold}
          >
            Nawy Unlocked Yield
          </Typography>
          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.sm.regular}
          >
            These numbers are not sent to the client.
          </Typography>
        </Box>
        <StyledTableContainer>
          <Table>
            <StyledTableHead>
              <TableRow>
                <StyledTableCell />
                <StyledTableCell>Monthly</StyledTableCell>
                <StyledTableCell>Annually</StyledTableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {yearlyData.map((data) => (
                <TableRow key={data.year}>
                  <StyledTableCell>Year {data.year}</StyledTableCell>
                  <StyledTableCell>
                    {formatCurrency(data.nawyMonthly)}
                  </StyledTableCell>
                  <StyledTableCell>
                    {formatCurrency(data.nawyYearly)}
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </Box>
    </Box>
  );
}
